/**
 * @file InactivityModal.tsx
 *
 * @description A modal component that displays a warning when user inactivity is detected
 * to prevent unauthorized access and ensure security for police officers' sessions.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

/**
 * Props for the InactivityModal component
 * @interface InactivityModalProps
 * @property {boolean} open - Controls whether the modal is displayed
 * @property {() => void} onContinue - Callback function when user chooses to continue their session
 * @property {() => void} onLogout - Callback function when user chooses to save and exit
 * @property {number} expiryTimestamp - Unix timestamp (in milliseconds) when the session will expire
 */
interface InactivityModalProps {
  open: boolean;
  onContinue: () => void;
  onLogout: () => Promise<void>;
  expiryTimestamp: number; // in milliseconds
}

/**
 * @name InactivityModal
 *
 * @description A modal component that warns users about impending session timeout due to inactivity
 *
 * @param {InactivityModalProps} props - Component props
 *
 * @returns {React.ReactElement} A dialog showing countdown and options to continue or exit
 */
export const InactivityModal: React.FC<InactivityModalProps> = ({
  open,
  onContinue,
  onLogout,
  expiryTimestamp,
}) => {
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);

  useEffect(() => {
    if (!open) return;

    // Calculate initial remaining time and set total time
    const now = Date.now() / 1000;
    const initialRemaining = Math.max(0, Math.floor((expiryTimestamp - now)));
    setRemainingTime(initialRemaining);
    
    // Set total time on first open
    if (totalTime === 0) {
      setTotalTime(initialRemaining);
    }

    // Update remaining time every second
    const interval = setInterval(async () => {
      const now = Date.now() / 1000;
      const remaining = Math.max(0, Math.floor((expiryTimestamp - now)));
      setRemainingTime(remaining);

      // Auto-logout when time expires
      if (remaining <= 0) {
        await onLogout();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  /**
   * @name formatTime
   *
   * @description Formats seconds into MM:SS display format
   *
   * @param {number} seconds - Time in seconds to format
   *
   * @returns {string} Formatted time string in MM:SS format
   */
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  // Calculate progress percentage for the progress bar
  const progressPercentage = totalTime > 0 ? (remainingTime / totalTime) * 100 : 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      aria-labelledby="inactivity-alert-dialog"
      PaperProps={{
        sx: {
          borderLeft: "5px solid #f44336",
          borderRadius: "4px",
        },
      }}
    >
      <DialogTitle
        id="inactivity-alert-dialog"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <WarningIcon color="error" />
        Timeout Warning
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Due to inactivity, you will be redirected to the preview page in:
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
            p: 2,
            bgcolor: "rgba(244, 67, 54, 0.1)",
            borderRadius: 1,
          }}
        >
          <Typography variant="h4" color="error" fontWeight="bold">
            {formatTime(remainingTime)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          color="error"
          sx={{ mb: 2, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2">
          For security, your editing session will be saved and exited after a
          period of inactivity. To continue working, please click the "Continue
          Editing" button.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onLogout} color="inherit">
          Save & Exit
        </Button>
        <Button
          onClick={onContinue}
          variant="contained"
          color="primary"
          autoFocus
        >
          Continue Editing
        </Button>
      </DialogActions>
    </Dialog>
  );
};
